import type { BookingLocale, BookingMessages } from "@/i18n/types";
import { de } from "@/i18n/de";
import type { OnlineBookingSeasonPeriod } from "@/types";
import {
  monthDayInRecurring,
  monthDayIsOvernightInRecurring,
} from "@shared/monthDayInRecurring";

function parseIsoDate(iso: string): { year: number; month: number; day: number } {
  const [y, m, d] = iso.split("-").map(Number);
  return { year: y ?? 0, month: m ?? 1, day: d ?? 1 };
}

export function isDateInBookingSeason(iso: string, season: OnlineBookingSeasonPeriod): boolean {
  const { month, day } = parseIsoDate(iso);
  if (season.mode === "fixed") {
    if (!season.start_date || !season.end_date) return false;
    return iso >= season.start_date && iso <= season.end_date;
  }
  if (
    season.start_month == null ||
    season.start_day == null ||
    season.end_month == null ||
    season.end_day == null
  ) {
    return false;
  }
  return monthDayInRecurring(
    month,
    day,
    season.start_month,
    season.start_day,
    season.end_month,
    season.end_day,
  );
}

/** Buchbare Nacht. Der Saison-Endtag ist nur Abreise, keine Anreise. */
function isOvernightInBookingSeason(iso: string, season: OnlineBookingSeasonPeriod): boolean {
  const { month, day } = parseIsoDate(iso);
  if (season.mode === "fixed") {
    if (!season.start_date || !season.end_date) return false;
    return iso >= season.start_date && iso < season.end_date;
  }
  if (
    season.start_month == null ||
    season.start_day == null ||
    season.end_month == null ||
    season.end_day == null
  ) {
    return false;
  }
  return monthDayIsOvernightInRecurring(
    month,
    day,
    season.start_month,
    season.start_day,
    season.end_month,
    season.end_day,
  );
}

export function isDateBookable(iso: string, seasons: OnlineBookingSeasonPeriod[]): boolean {
  if (seasons.length === 0) return true;
  return seasons.some((season) => isOvernightInBookingSeason(iso, season));
}

export function isBookingPeriodInSeasons(
  arrival: string,
  departure: string,
  seasons: OnlineBookingSeasonPeriod[],
): boolean {
  if (!arrival || !departure || departure <= arrival) return false;
  if (seasons.length === 0) return true;
  let current = arrival;
  while (current < departure) {
    if (!isDateBookable(current, seasons)) return false;
    const [y, m, d] = current.split("-").map(Number);
    const next = new Date(y, m - 1, d + 1);
    const ny = next.getFullYear();
    const nm = String(next.getMonth() + 1).padStart(2, "0");
    const nd = String(next.getDate()).padStart(2, "0");
    current = `${ny}-${nm}-${nd}`;
  }
  return true;
}

function seasonNamesSuffix(locale: BookingLocale): string {
  return locale === "en"
    ? " Bookable stays only during:"
    : " Buchbar sind nur Aufenthalte in:";
}

export function bookingSeasonErrorMessage(
  seasons: OnlineBookingSeasonPeriod[],
  messages: BookingMessages["season"] = de.season,
  locale: BookingLocale = "de",
): string {
  if (seasons.length === 0) {
    return messages.outsideSeason;
  }
  const names = seasons
    .slice(0, 3)
    .map((s) => s.name)
    .filter(Boolean)
    .join(", ");
  const suffix = seasons.length > 3 ? " …" : "";
  return `${messages.outsideSeason}${seasonNamesSuffix(locale)} ${names}${suffix}.`;
}
